import { Filter, Search } from 'lucide-react';
import type { Execution } from '../../types/api';

interface ExecutionFiltersProps {
  search: string;
  status: string;
  flowId: string;
  dateRange: { start: string; end: string };
  showFilters: boolean;
  onSearchChange: (value: string) => void;
  onStatusChange: (status: Execution['status'] | '') => void;
  onFlowIdChange: (flowId: string) => void;
  onDateRangeChange: (field: 'start' | 'end', value: string) => void;
  onToggleFilters: () => void;
  onResetFilters: () => void;
}

/**
 * Search and filter controls for executions
 */
export function ExecutionFilters({
  search,
  status,
  flowId,
  dateRange,
  showFilters,
  onSearchChange,
  onStatusChange,
  onFlowIdChange,
  onDateRangeChange,
  onToggleFilters,
  onResetFilters,
}: ExecutionFiltersProps) {
  return (
    <>
      {/* Search and Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by execution ID or flow name..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="px-3 py-2 pl-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors"
            title="Search executions by ID, workflow name, or any text in the execution details"
          />
        </div>
        <button
          onClick={onToggleFilters}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          title="Show/hide filters to narrow down execution results"
        >
          <Filter className="mr-2 w-4 h-4" />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="p-4 mt-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => onStatusChange(e.target.value as Execution['status'] | '')}
                className="px-3 py-2 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                title="Filter executions by their current status (pending, running, completed, failed, or cancelled)"
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="running">Running</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                Flow ID
              </label>
              <input
                type="text"
                placeholder="Filter by flow ID"
                value={flowId}
                onChange={(e) => onFlowIdChange(e.target.value)}
                className="px-3 py-2 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                title="Filter executions to show only those from a specific workflow ID"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                Start Date
              </label>
              <input
                type="datetime-local"
                value={dateRange.start}
                onChange={(e) => onDateRangeChange('start', e.target.value)}
                className="px-3 py-2 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                title="Filter executions that started after this date and time"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                End Date
              </label>
              <input
                type="datetime-local"
                value={dateRange.end}
                onChange={(e) => onDateRangeChange('end', e.target.value)}
                className="px-3 py-2 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                title="Filter executions that started before this date and time"
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={onResetFilters}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
              title="Clear all filters and show all executions"
            >
              Reset Filters
            </button>
          </div>
        </div>
      )}
    </>
  );
} 